import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import { CalendarCheck, Megaphone, Pin, ChevronRight, Network } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useCollection } from '../../hooks/useFirestore'
import { formatDate, todayISO } from '../../utils/helpers'
import { Badge, Card } from '../../components/common'

const WorkersDashboard = () => {
  const { user, userProfile } = useAuth()
  const myDepartments = userProfile?.departmentIds || []

  const { data: assignments, loading: loadingAssignments } = useCollection('assignments', {
    orderByField: 'date',
    orderDirection: 'asc',
    limitCount: 5,
    whereClauses: [
      { field: 'workerUid', operator: '==', value: user?.uid },
      { field: 'date', operator: '>=', value: todayISO() },
    ],
  })

  const { data: announcements } = useCollection('announcements', {
    orderByField: 'publishedAt',
    orderDirection: 'desc',
    whereClause: { field: 'isPublished', operator: '==', value: true },
  })

  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const visibleAnnouncements = useMemo(() => {
    const list = (announcements || []).filter((a) => {
      const targets = a.departmentIds || []
      return targets.length === 0 || targets.some((slug) => myDepartments.includes(slug))
    })
    return [...list].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).slice(0, 5)
  }, [announcements, myDepartments])

  const myDeptDocs = (departments || []).filter((d) => myDepartments.includes(d.id))

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-heading font-bold text-dark-100">
          Welcome, {userProfile?.displayName?.split(' ')[0] || 'worker'}
        </h1>
        <p className="text-gray-500">Here's what's coming up for you</p>
      </div>

      {/* Upcoming assignments */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-primary-600" />
            Your Next Assignments
          </h2>
          <Link
            to="/workers/schedule"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            Full schedule <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="space-y-2">
          {(assignments || []).map((a) => (
            <div
              key={a.id}
              className="bg-white rounded-xl shadow-soft p-4 flex items-center justify-between gap-3"
            >
              <div>
                <p className="font-medium text-dark-100">
                  {formatDate(a.date, 'EEEE, MMMM d')}
                  {a.serviceTime ? ` · ${a.serviceTime}` : ''}
                </p>
                <p className="text-sm text-gray-600">
                  {a.serviceName}
                  {a.duty ? ` — ${a.duty}` : ''}
                </p>
                {a.notes && <p className="text-sm text-gray-400 mt-1">{a.notes}</p>}
              </div>
              <Badge variant="primary" size="sm">{a.departmentName}</Badge>
            </div>
          ))}
          {!loadingAssignments && (assignments || []).length === 0 && (
            <Card className="text-center py-8 text-gray-500">
              No upcoming assignments — enjoy the rest!
            </Card>
          )}
        </div>
      </section>

      {/* Announcements */}
      <section>
        <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2 mb-3">
          <Megaphone className="w-5 h-5 text-primary-600" />
          Announcements
        </h2>
        <div className="space-y-2">
          {visibleAnnouncements.map((a) => (
            <div key={a.id} className="bg-white rounded-xl shadow-soft p-4">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {a.pinned && <Pin className="w-4 h-4 text-accent-600" />}
                <p className="font-medium text-dark-100">{a.title}</p>
                {(a.departmentIds || []).length > 0 && (
                  <Badge variant="primary" size="sm">
                    {(a.departmentIds || []).join(', ')}
                  </Badge>
                )}
              </div>
              <div className="text-sm text-gray-600 prose prose-sm max-w-none">
                <ReactMarkdown>{a.body}</ReactMarkdown>
              </div>
            </div>
          ))}
          {visibleAnnouncements.length === 0 && (
            <Card className="text-center py-8 text-gray-500">No announcements right now</Card>
          )}
        </div>
      </section>

      {/* My departments */}
      {myDeptDocs.length > 0 && (
        <section>
          <h2 className="font-heading font-semibold text-dark-100 flex items-center gap-2 mb-3">
            <Network className="w-5 h-5 text-primary-600" />
            My Departments
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {myDeptDocs.map((dept) => (
              <Link
                key={dept.id}
                to={`/workers/departments/${dept.id}`}
                className="bg-white rounded-xl shadow-soft p-4 hover:shadow-lg transition-shadow flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-dark-100">{dept.name}</p>
                  {dept.leaderName && (
                    <p className="text-sm text-gray-500">Led by {dept.leaderName}</p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export default WorkersDashboard
